import TipoAcaoExterna from "../../../Tarefas/TipoAcaoExterna";
import TarefaTipoCondicional from "../../../Tarefas/TipoCondicional";
import TarefaTipoSimples from "../../../Tarefas/TipoSimples";
import React, { useState, useCallback, useContext } from "react";
import { StepperChildrenProps, TSearchUltimaResposta } from ".";
import { AuthContext } from "../../../../../contexts/AuthContext";
import useUsuarioCorresponde from "../../../../../lib/useUsuarioCorrespondePerfil";
import { FormProvider, useForm } from "react-hook-form";
import DialogCancelarTarefa from "../../../Dialogs/DialogCancelarTarefa";
import { IDataDetalharProcesso } from "../../../../../types/api/bpm/detalhar-processo";
import { useTarefaMutations, ResolverTarefaInput } from "../../../../../hooks/useTarefaMutation";
import { acharArquivoNoObj, removerArquivosDoObj } from "./helpers";

export default function ContentTarefas({
  tarefa,
  handleSearchUltimaResposta,
  extras,
  usuarioSolicitante,
}: ContentTarefasProps) {

  const { getUserData, possuiPerfilId } = useContext(AuthContext);
  const userData = getUserData();

  const usuarioCorresponde = useUsuarioCorresponde(
    tarefa.perfil?.id,
    tarefa.usuario?.uuid
  );
  const idTarefa = tarefa.id;
  const idProcesso = tarefa.processo_id;
  const usuarioPodeRealizar =
    tarefa.usuario?.uuid === userData?.uuid ||
    possuiPerfilId(tarefa.perfil?.id);
  const precisaFormulario: string[] = tarefa.extras?.precisa_formulario ?? [];

  const solicitantePodeRealizar =
    !(tarefa.extras?.bloquear_solicitante === "sim" &&
      usuarioSolicitante === userData?.nome);

  const [openModal, setOpenModal] = useState(false);

  const {
    mutateResolverTarefa,
    isLoadingResolverTarefa,
    mutateDevolverTarefa,
    isLoadingDevolverTarefa,
    mutateReprocessarTarefa,
    isLoadingReprocessarTarefa,
  } = useTarefaMutations();

  const defaultValues: { [key: string]: any } = {
    extras: tarefa.extras,
    observacoes: "",
    anexos: [] as File[],
    default: {}
  };

  if (tarefa.formulario) {
    let keysObj: string[] = [];
    tarefa.formulario.spec.forEach((item) => {
      defaultValues[item.name] = item.default || undefined;
      keysObj.push(item.name);
    });

    const preFilledValues = handleSearchUltimaResposta(keysObj);
    preFilledValues.forEach((element) => {
      if (!element) return;
      defaultValues[element.key] = element.value;
    });
  }

  const methods = useForm({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });
  const { getValues } = methods;

  const handleResolverTarefa = useCallback(
    (observacoes: string, resposta: string, anexos: File[]) => {
      let formData = new FormData();

      const arquivosNoForm = acharArquivoNoObj(getValues());

      const params: ResolverTarefaInput = {
        tarefa_id: idTarefa,
        observacoes: observacoes,
        resposta: resposta,
      };

      if (arquivosNoForm.length > 0) {
        arquivosNoForm.forEach(({ propertyName, file }) => {
          formData.append(propertyName, file);
        });

        const respostaFiles: string[] = [];
        arquivosNoForm.forEach(({ propertyName }) => {
          params[propertyName] = formData.get(propertyName);
          respostaFiles.push(propertyName);
        });
        params.resposta_files = respostaFiles;

        params.resposta = removerArquivosDoObj(resposta, arquivosNoForm);
      }

      if (anexos.length > 0) {
        anexos.forEach((file, index) => {
          formData.append(`anexos[${index}]`, file);
        });

        const anexosArray: (File | null)[] = [];
        anexos.forEach((_, index) => {
          anexosArray.push(formData.get(`anexos[${index}]`) as File | null);
        });
        params.anexos = anexosArray;
      }

      mutateResolverTarefa(params);
    },
    [mutateResolverTarefa, idTarefa, getValues]
  );

  const handleDevolverTarefa = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleDialogOK = (motivoCancelamento: string) => {
    mutateDevolverTarefa({ idProcesso: Number(idProcesso), motivoCancelamento });
    setOpenModal(false);
  };

  const handleReprocessarTarefa = useCallback(
    (forcar: boolean) => {
      const params = {
        tarefa_id: idTarefa,
        forcar,
        observacoes: "",
      };

      mutateReprocessarTarefa(params);
    },
    [mutateReprocessarTarefa, idTarefa]
  );

  if (!usuarioCorresponde) return <React.Fragment />;

  let renderComponent = <></>;

  if (tarefa.tipo === "Condicional" || tarefa.tipo === "Condicional automática")
    renderComponent = (
      <TarefaTipoCondicional
        isLoading={isLoadingResolverTarefa}
        formularioDyn={tarefa.formulario}
        handleClick={(observacoes, anexos, resposta) =>
          handleResolverTarefa(observacoes, resposta, anexos)
        }
        handleClickDevolver={handleDevolverTarefa}
        opcoes={tarefa.opcoes}
        precisaFormulario={precisaFormulario}
        solicitantePodeRealizar={solicitantePodeRealizar}
        extras={extras}
      />
    );

  if (tarefa.tipo === "Simples")
    renderComponent = (
      <TarefaTipoSimples
        formularioDyn={tarefa.formulario}
        isLoading={isLoadingResolverTarefa}
        handleClick={(observacoes, anexos, resposta) =>
          handleResolverTarefa(observacoes, resposta, anexos)
        }
        handleClickDevolver={handleDevolverTarefa}
        extras={extras}
      />
    );

  if (tarefa.tipo === "Ação externa")
    renderComponent = (
      <TipoAcaoExterna
        {...{ usuarioPodeRealizar }}
        listaError={tarefa.erro_detalhe?.restricoes || null}
        isLoading={isLoadingReprocessarTarefa}
        actions={tarefa.extras.acao_externa}
        onAction={(forcar: boolean) => handleReprocessarTarefa(forcar)}
      />
    );

  return (
    <FormProvider {...methods}>
      {renderComponent}
      <DialogCancelarTarefa
        open={openModal}
        handleClose={() => setOpenModal(false)}
        handleOK={handleDialogOK}
        isLoading={isLoadingDevolverTarefa}
      />
    </FormProvider>
  );
}

interface ContentTarefasProps extends StepperChildrenProps {
  extras: IDataDetalharProcesso["extras"];
  handleSearchUltimaResposta: (keys: string[]) => TSearchUltimaResposta;
  usuarioSolicitante: string;
}